import * as React from 'react';
import { Box, Grid, Typography, Stack } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import { connect } from 'react-redux';
import { motion } from 'framer-motion';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from 'recharts';
import { GenerateCampaignInvitationModal } from './components';
import { getWhatsappCampaigns, downloadCampaignInvitations } from '../../app/store/actions/adminActions';
import {
    PlatformPage,
    PlatformPageHeader,
    PlatformCard,
    PlatformButton,
    PlatformStatsGrid,
    PlatformStatCard,
    PlatformLoadingBlock,
} from '../../app/components/platform';
import { platformColors, platformFonts } from '../../app/components/tickitPlatformTheme';

const CHART_COLORS = [
    platformColors.brand,
    platformColors.accent,
    platformColors.success,
    platformColors.info,
];

export const CampaignDashboard = ({ isLoading, getWhatsappCampaigns, campaignData, downloadCampaignInvitations }) => {
    const navigate = useNavigate();
    const { id } = useParams();

    const [modals, setModals] = React.useState({ generateInvitations: false });

    React.useEffect(() => {
        getWhatsappCampaigns({ campaignId: id });
    }, [id, getWhatsappCampaigns]);

    if (isLoading) {
        return (
            <PlatformPage>
                <PlatformLoadingBlock />
            </PlatformPage>
        );
    }

    const stats = campaignData?.statistics ?? {};
    const rsvpChartData = [
        { name: 'Accepted', value: stats.accepted ?? 0 },
        { name: 'Rejected', value: stats.rejected ?? 0 },
        { name: 'Plus Ones', value: stats.plusOnes ?? 0 },
    ];

    return (
        <PlatformPage>
            <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35 }}>
                <PlatformPageHeader
                    title={campaignData?.campaign?.title || 'Campaign'}
                    subtitle={campaignData?.campaign?.description}
                />

                <PlatformStatsGrid columns={3}>
                    <PlatformStatCard label="Invitations sent" value={stats.sent ?? 0} />
                    <PlatformStatCard label="Delivered" value={stats.delivered ?? 0} />
                    <PlatformStatCard label="Read" value={stats.read ?? 0} />
                    <PlatformStatCard label="Failed" value={stats.failed ?? 0} />
                    <PlatformStatCard label="Accepted" value={stats.accepted ?? 0} />
                    <PlatformStatCard label="Total coming" value={stats.totalComing ?? 0} />
                </PlatformStatsGrid>

                <Grid container spacing={2.5}>
                    <Grid item xs={12} md={4}>
                        <PlatformCard title="Campaign actions">
                            <GenerateCampaignInvitationModal
                                open={modals.generateInvitations}
                                handleClose={() => setModals({ ...modals, generateInvitations: false })}
                            />
                            <Stack spacing={1.5}>
                                <PlatformButton
                                    variant="primary"
                                    onClick={() => setModals({ ...modals, generateInvitations: true })}
                                >
                                    Generate invitations
                                </PlatformButton>
                                <PlatformButton variant="ghost" onClick={() => navigate(`/campaign/${id}/invitations`)}>
                                    View campaign invitations
                                </PlatformButton>
                                <PlatformButton variant="ghost" onClick={() => downloadCampaignInvitations({ campaignId: id })}>
                                    Download invitations
                                </PlatformButton>
                            </Stack>
                        </PlatformCard>
                    </Grid>

                    <Grid item xs={12} md={8}>
                        <PlatformCard title="RSVP breakdown">
                            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, mb: 2 }}>
                                <Box sx={{ p: 2, borderRadius: 2, border: `1px solid ${platformColors.border}` }}>
                                    <Typography
                                        sx={{
                                            fontFamily: platformFonts.body,
                                            fontSize: '0.7rem',
                                            fontWeight: 600,
                                            textTransform: 'uppercase',
                                            color: 'var(--platform-text-3)',
                                            mb: 0.5,
                                        }}
                                    >
                                        Total invitations sent
                                    </Typography>
                                    <Typography sx={{ fontFamily: platformFonts.display, fontWeight: 700, fontSize: '1.75rem', color: 'var(--platform-text)' }}>
                                        {stats.sent ?? 0}
                                    </Typography>
                                </Box>
                                <Box sx={{ p: 2, borderRadius: 2, border: `1px solid ${platformColors.border}` }}>
                                    <Typography
                                        sx={{
                                            fontFamily: platformFonts.body,
                                            fontSize: '0.7rem',
                                            fontWeight: 600,
                                            textTransform: 'uppercase',
                                            color: 'var(--platform-text-3)',
                                            mb: 0.5,
                                        }}
                                    >
                                        Total people coming
                                    </Typography>
                                    <Typography sx={{ fontFamily: platformFonts.display, fontWeight: 700, fontSize: '1.75rem', color: 'var(--platform-text)' }}>
                                        {stats.totalComing ?? 0}
                                    </Typography>
                                </Box>
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                                <ResponsiveContainer width="100%" height={300}>
                                    <PieChart>
                                        <Pie
                                            dataKey="value"
                                            isAnimationActive
                                            data={rsvpChartData}
                                            cx="50%"
                                            cy="50%"
                                            innerRadius={60}
                                            outerRadius={90}
                                            label
                                            paddingAngle={5}
                                        >
                                            {rsvpChartData.map((entry, index) => (
                                                <Cell key={`rsvp-${entry.name}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                                            ))}
                                        </Pie>
                                        <Tooltip />
                                    </PieChart>
                                </ResponsiveContainer>
                            </Box>
                        </PlatformCard>
                    </Grid>
                </Grid>
            </motion.div>
        </PlatformPage>
    );
};

const mapStateToProps = (state) => ({
    isLoading: state?.records?.isLoading,
    campaignData: state?.records?.results?.campaignData,
});

export default connect(mapStateToProps, { getWhatsappCampaigns, downloadCampaignInvitations })(CampaignDashboard);
